export enum ConvoAIWebSocketServerMessageType {
	TranscriptionResponseDelta = 'transcription.response.delta',
	TranscriptionItemCreated = 'transcription.item.created',
}

export enum ConvoAIWebSocketClientMessageType {
	AudioBufferAppend = 'audio.buffer.append',
	AudioBufferCommit = 'audio.buffer.commit',
	AudioSessionUpdate = 'audio.session.update',
}

export type ConvoAIWebSocketServerMessage =
	| {
			content_index: number;
			delta: string;
			type: ConvoAIWebSocketServerMessageType.TranscriptionResponseDelta;
	  }
	| {
			type: ConvoAIWebSocketServerMessageType.TranscriptionItemCreated;
	  };

export class ConvoAIWebSocketAPI {
	private socket: WebSocket;

	constructor(
		cloudID: string,
		product: string,
		experience: string,
		private readonly onMessage: (event: ConvoAIWebSocketServerMessage) => void,
	) {
		this.socket = new WebSocket(
			`/wss/${cloudID}/ai-feature/audio/realtime-transcriptions?product=${product}&experience=${experience}`,
		);
		this.socket.addEventListener('message', this.onServerMessage);
	}

	updateTranscriptionConfig(
		instructions: string = `Transcribe speech to text. You may get wake words like 'Hey Rovo!' or 'Hey Atlassian!' or 'Rovo, '. Do not include the waking phrase at the beginning of transcription`,
		noise_detection: string = 'near_field',
		vad_type: string = 'semantic_vad',
		eagerness: string = 'medium',
		model: string = 'gpt-4o-transcribe',
		include: string[] = ['item.input_audio_transcription.logprobs'],
	): void {
		this.socket.send(
			JSON.stringify({
				type: 'audio.session.update',
				model,
				include,
				vad_type,
				eagerness,
				instructions,
				noise_detection,
			}),
		);
	}

	sendAudio(audio: string): void {
		this.socket.send(
			JSON.stringify({ audio, type: ConvoAIWebSocketClientMessageType.AudioBufferAppend }),
		);
	}

	commitAudio(): void {
		this.socket.send(JSON.stringify({ type: ConvoAIWebSocketClientMessageType.AudioBufferCommit }));
	}

	connect(): Promise<ConvoAIWebSocketAPI> {
		return new Promise((resolve, reject) => {
			this.socket.onopen = () => resolve(this);
			this.socket.onerror = (event) => reject(event);
		});
	}

	onServerMessage = (event: MessageEvent): void => {
		const data = JSON.parse(event.data) as ConvoAIWebSocketServerMessage;
		this.onMessage(data);
	};

	disconnect(): void {
		this.socket?.close();
	}

	static create(
		cloudID: string,
		product: string,
		experience: string,
		onMessage: (event: ConvoAIWebSocketServerMessage) => void,
	): Promise<ConvoAIWebSocketAPI> {
		const api = new ConvoAIWebSocketAPI(cloudID, product, experience, onMessage);
		return api.connect();
	}
}
